/**
 * BusinessCanvas
 *
 * A component for displaying and organizing business models and plans
 *
 * Primary responsibilities:
 * - Visualize business model components in a structured format
 * - Allow exploration of different aspects of a business plan
 * - Organize key business segments in a visual canvas
 * - Support both lean canvas and traditional business model formats
 */

import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { IconBuildingStore, IconChevronDown, IconChevronRight, IconEdit } from '@tabler/icons-react'

// Section represents a key area of the business model
export interface CanvasSection {
  id: string
  title: string
  content: string[] | { key: string; value: string }[]
  icon?: string
  color?: string
  type?: 'list' | 'keyValue' | 'text'
}

// Canvas model type (business model canvas or lean canvas)
export type CanvasType = 'businessModel' | 'leanStartup' | 'valueProposition' | 'custom'

// Main business canvas data
export interface BusinessCanvasData {
  title: string
  description?: string
  type: CanvasType
  sections: CanvasSection[]
  layout?: 'standard' | 'compact' | 'expanded'
}

interface BusinessCanvasProps {
  data: BusinessCanvasData
  className?: string
  editable?: boolean
  onSectionEdit?: (sectionId: string, content: any) => void
}

export const BusinessCanvas: React.FC<BusinessCanvasProps> = ({
  data,
  className,
  editable = false,
  onSectionEdit
}) => {
  // Ensure sections is an array
  const sections = Array.isArray(data.sections) ? data.sections : []

  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(sections.map((s) => s.id))
  )
  const [hoverSection, setHoverSection] = useState<string | null>(null)

  // Toggle section expansion
  const toggleSection = (sectionId: string) => {
    const newExpandedSections = new Set(expandedSections)
    if (newExpandedSections.has(sectionId)) {
      newExpandedSections.delete(sectionId)
    } else {
      newExpandedSections.add(sectionId)
    }
    setExpandedSections(newExpandedSections)
  }

  // Get section color based on type or custom color
  const getSectionColor = (section: CanvasSection) => {
    if (section.color) return section.color

    // Default colors based on common business model canvas sections
    switch (section.id) {
      case 'keyPartners':
        return '#3b82f6' // Blue
      case 'keyActivities':
        return '#10b981' // Emerald
      case 'keyResources':
        return '#f59e0b' // Amber
      case 'valueProposition':
        return '#ef4444' // Red
      case 'customerRelationships':
        return '#8b5cf6' // Violet
      case 'channels':
        return '#ec4899' // Pink
      case 'customerSegments':
        return '#6366f1' // Indigo
      case 'costStructure':
        return '#64748b' // Slate
      case 'revenueStreams':
        return '#22c55e' // Green
      default:
        return '#6b7280' // Gray
    }
  }

  // Expand all sections
  const expandAll = () => {
    setExpandedSections(new Set(sections.map((s) => s.id)))
  }

  // Collapse all sections
  const collapseAll = () => {
    setExpandedSections(new Set())
  }

  // Custom grid layouts based on canvas type
  const getGridLayout = () => {
    if (data.layout === 'compact') return 'grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3'

    switch (data.type) {
      case 'businessModel':
        return 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 md:grid-rows-3 gap-2 sm:gap-3'
      case 'leanStartup':
        return 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 md:grid-rows-3 gap-2 sm:gap-3'
      case 'valueProposition':
        return 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 md:grid-rows-2 gap-2 sm:gap-3'
      default:
        return 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3'
    }
  }

  // Get section span based on canvas type and section id
  const getSectionSpan = (section: CanvasSection) => {
    if (data.layout === 'compact') return ''

    // Special spans for business model canvas
    if (data.type === 'businessModel') {
      switch (section.id) {
        case 'valueProposition':
          return 'sm:col-span-2 md:col-span-1 md:row-span-3'
        case 'costStructure':
          return 'sm:col-span-2 md:col-span-3 lg:col-span-2'
        case 'revenueStreams':
          return 'sm:col-span-2 md:col-span-3 lg:col-span-3'
        default:
          return ''
      }
    }

    // Special spans for lean startup canvas
    if (data.type === 'leanStartup') {
      switch (section.id) {
        case 'problem':
          return 'md:row-span-2'
        case 'solution':
          return 'md:row-span-2'
        case 'uniqueValueProposition':
          return 'sm:col-span-2 md:col-span-1 md:row-span-3'
        case 'metrics':
          return 'sm:col-span-2 md:col-span-2'
        default:
          return ''
      }
    }

    return ''
  }

  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      <div className="flex flex-col gap-2 border-b p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <IconBuildingStore size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">{data.title}</h3>
        </div>

        <div className="flex gap-2 self-end sm:self-auto">
          <Button variant="ghost" size="sm" onClick={expandAll} className="text-xs">
            Expand All
          </Button>
          <Button variant="ghost" size="sm" onClick={collapseAll} className="text-xs">
            Collapse All
          </Button>
        </div>
      </div>

      {data.description && (
        <div className="text-muted-foreground border-b px-3 py-2 text-xs sm:px-4">
          {data.description}
        </div>
      )}

      <div className="p-2 sm:p-4">
        <div className={getGridLayout()}>
          {sections.map((section) => {
            const isExpanded = expandedSections.has(section.id)
            const sectionColor = getSectionColor(section)
            const sectionSpan = getSectionSpan(section)

            return (
              <div
                key={section.id}
                className={cn(
                  'flex flex-col overflow-hidden rounded-md border',
                  'transition-all duration-200',
                  sectionSpan,
                  hoverSection === section.id ? 'shadow-md' : ''
                )}
                onMouseEnter={() => setHoverSection(section.id)}
                onMouseLeave={() => setHoverSection(null)}
                style={{ borderColor: `${sectionColor}80` }}
              >
                <div
                  className="flex cursor-pointer items-center justify-between p-2 sm:p-3"
                  onClick={() => toggleSection(section.id)}
                  style={{ backgroundColor: `${sectionColor}20` }}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="size-2 shrink-0 rounded-full sm:size-3"
                      style={{ backgroundColor: sectionColor }}
                    ></div>
                    <h4 className="text-xs font-medium sm:text-sm">{section.title}</h4>
                  </div>
                  <div className="flex items-center">
                    {editable && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="mr-1 size-5 sm:size-6"
                        onClick={(e) => {
                          e.stopPropagation()
                          onSectionEdit?.(section.id, section.content)
                        }}
                      >
                        <IconEdit size={12} className="sm:hidden" />
                        <IconEdit size={14} className="hidden sm:block" />
                      </Button>
                    )}
                    {isExpanded ? (
                      <IconChevronDown size={14} className="text-muted-foreground sm:hidden" />
                    ) : (
                      <IconChevronRight size={14} className="text-muted-foreground sm:hidden" />
                    )}
                    {isExpanded ? (
                      <IconChevronDown
                        size={16}
                        className="text-muted-foreground hidden sm:block"
                      />
                    ) : (
                      <IconChevronRight
                        size={16}
                        className="text-muted-foreground hidden sm:block"
                      />
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className="p-2 text-xs sm:p-3 sm:text-sm">
                    {/* Handle different content types */}
                    {section.type === 'keyValue' &&
                      Array.isArray(section.content) &&
                      section.content.every(
                        (item) => typeof item === 'object' && 'key' in item && 'value' in item
                      ) && (
                        <div className="space-y-1 sm:space-y-2">
                          {(section.content as { key: string; value: string }[]).map((item, i) => (
                            <div key={`${section.id}-kv-${i}`}>
                              <div className="text-xs font-medium">{item.key}</div>
                              <div className="text-muted-foreground text-xs">{item.value}</div>
                            </div>
                          ))}
                        </div>
                      )}

                    {(section.type === 'list' || !section.type) &&
                      Array.isArray(section.content) &&
                      section.content.every((item) => typeof item === 'string') && (
                        <ul className="list-disc space-y-1 pl-4 sm:pl-5">
                          {(section.content as string[]).map((item, i) => (
                            <li
                              key={`${section.id}-list-${i}`}
                              className="text-muted-foreground text-xs"
                            >
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}

                    {section.type === 'text' && typeof section.content === 'string' && (
                      <p className="text-muted-foreground text-xs">{section.content}</p>
                    )}

                    {section.type === 'text' &&
                      Array.isArray(section.content) &&
                      section.content.every((item) => typeof item === 'string') && (
                        <div className="space-y-1 sm:space-y-2">
                          {(section.content as string[]).map((paragraph, i) => (
                            <p
                              key={`${section.id}-para-${i}`}
                              className="text-muted-foreground text-xs"
                            >
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
